<template>
  <div class="timeline-insights">
    <div class="widget-wrapper">
      <div class="d-flex align-items-center my-10 p-20 z-1 relative">
        <div class="timeline-metrics d-flex align-items-center flex-auto flex-wrap gap-20">
          <label
            v-for="(metric, key) in metrics"
            :key="key"
            class="custom-checkbox-wrapper"
          >
            <input
              :checked="selectedMetricKeys.includes(key)"
              type="checkbox"
              @change="toggleMetric(key)"
            />
            <div
              class="custom-checkbox-box"
              :class="`checkbox-${key}`"
            ></div>
            <span class="timeline-header d-inline-flex align-items-center">
              {{ metric.title }}
              <i
                v-if="metric.info"
                v-tooltip="tooltipOptions"
                :title="metric.info"
                class="hi hi-info hi-14 ml-5"
              ></i>
            </span>
          </label>
        </div>
        <div class="d-md-block border-left mx-10 h-20"></div>

        <AppToggleDropdown
          title="Show or hide columns"
          trigger-classes="btn btn btn-outline-secondary"
        >
          <template #trigger>
            <i class="hi hi-columns hi-14" />
          </template>
          <template #header>Table columns</template>
          <AppColumnToggle
            :active="showTotalHours"
            column="total_hours"
            @change="toggleTotalHours"
          >
            Total hours
          </AppColumnToggle>
        </AppToggleDropdown>
      </div>
      <!-- Show chart when metrics are selected -->
      <InsightsTimelineChart
        v-if="selectedMetricKeys.length > 0"
        :data="data"
        :date="date"
        :loading="isLoading"
        :rows-loading="isRowsLoading"
        :selected-metrics="selectedMetricKeys"
        :show-total-hours="showTotalHours"
        :sort="sortOptions"
        :time-zone="timeZone"
        :user-data="widgetData.user"
        @sort="sortMetrics"
        @timeline-user-selected="handleTimelineUserSelected"
      />
      <div
        v-else
        class="timeline-empty-state"
      >
        <InsightsNoData>
          <template #title>No metrics selected</template>
          <template #description> Please select at least one metric to view the timeline </template>
        </InsightsNoData>
      </div>
    </div>
    <AppPagination
      v-if="data.length && pagination && pagination.total_pages > 1"
      :loading="isLoading || isRowsLoading"
      :pagination="pagination"
      :resource="{ one: userId ? 'date' : 'member', many: userId ? 'dates' : 'members' }"
      @page-changed="onPageChanged"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { INSIGHTS } from '~/app_constants';
import { getInsightsPreference, setInsightsPreference } from '~/utils/insights_utils';
import InsightsNoData from './InsightsNoData.vue';
import InsightsTimelineChart from './InsightsTimelineChart.vue';
import AppPagination from '~/components/AppPagination.vue';
import { InsightsFilters, InsightsTracking } from '~/mixins';
import dayjs from 'dayjs';

const DEFAULT_METRICS = ['tracked', 'manual', 'idle', 'active'];
const REFRESH_INTERVAL = 60 * 1000; // 1 minute
const MAX_CACHE_SIZE = 10;
// Mapping between UI metric names and API metric names
const METRIC_API_MAPPING = {
  tracked: 'active', // Backend returns tracked time data under 'active_time' key
};
// Full load shows skeleton, rows load shows spinner over existing data
const LOAD_STATE = {
  IDLE: 'idle',
  LOADING_FULL: 'loading_full',
  LOADING_ROWS: 'loading_rows'
};

export default {
  name: 'InsightsTimeline',
  components: {
    InsightsTimelineChart,
    InsightsNoData,
    AppPagination,
  },
  mixins: [InsightsFilters, InsightsTracking],
  emits: ['loading-changed', 'timeline-user-selected'],
  data() {
    const saved = getInsightsPreference(this.$store.getters.organizationId, 'timeline_metrics');
    const initialMetrics = Array.isArray(saved) && saved.length ? saved : DEFAULT_METRICS;

    return {
      loadState: LOAD_STATE.IDLE,
      queuedLoad: null, // Stores pending request when one is in-flight
      showTotalHours: true,
      metrics: INSIGHTS.timeline.metrics,
      selectedMetricKeys: initialMetrics,
      refreshTimer: null,
      sortOptions: {
        sort_by: 'member',
        sort_order: 'asc',
      },
      tooltipOptions: {
        placement: 'top',
      },
      currentPage: 1,
      cache: {},
      displayData: [],
      displayPagination: null, // Cached pagination for instant cache hits
      queuedLoads: [],
      isProcessingQueuedLoads: false,
      hasInitializedContext: false,
    };
  },
  computed: {
    ...mapGetters([
      'userId',
      'teamId',
      'filters',
      'widgets',
      'organizationId',
      'organization',
      'timeZone',
      'isMembersTimeZoneMode',
    ]),
    isLoading() {
      return this.loadState === LOAD_STATE.LOADING_FULL
        || this.queuedLoad?.isFullLoad === true
        || this.queuedLoads.some(({ isFullLoad }) => isFullLoad === true);
    },
    isRowsLoading() {
      return this.loadState === LOAD_STATE.LOADING_ROWS
        || this.queuedLoad?.isFullLoad === false
        || this.queuedLoads.some(({ isFullLoad }) => isFullLoad === false);
    },
    isAnyLoading() {
      return this.loadState !== LOAD_STATE.IDLE;
    },
    widgetData() {
      // Use bracket notation instead of lodash get to maintain reactivity
      return this.widgets[this.widgetKey] || {};
    },
    data() {
      return this.displayData;
    },
    cacheKey() {
      const metrics = this.normalizeMetrics(this.selectedMetricKeys).sort().join(',');

      return `${metrics}|${this.currentPage}|${this.sortOptions.sort_by}|${this.sortOptions.sort_order}`;
    },
    pagination() {
      return this.displayPagination || this.widgetData.pagination || {};
    },
    date() {
      return this.filters.date.from;
    },
    widgetKey() {
      return this.userId ? INSIGHTS.api.insightsTimeline.user : INSIGHTS.api.insightsTimeline.team;
    },
    // Individual and team views sort by different columns (date vs member), so only a change of
    // shape between the two - not a zone/date change within the same shape - should reset sort.
    scopeKey() {
      return `${this.userId || 'all'}-${this.teamId}`;
    },
    contextKey() {
      return `${this.scopeKey}-${this.filters?.date?.from || 'no-date'}-${this.filters?.date?.to || 'no-date'}-${this.timeZone}-${this.isMembersTimeZoneMode}`;
    },
  },
  watch: {
    scopeKey() {
      this.sortOptions = this.userId
        ? { sort_by: 'date', sort_order: 'desc' }
        : { sort_by: 'member', sort_order: 'asc' };
    },
    contextKey() {
      this.handleContextChange();
    },
    selectedMetricKeys: {
      handler(newKeys) {
        if (newKeys.length) {
          setInsightsPreference(this.organizationId, 'timeline_metrics', newKeys);
        }
        this.loadData(false, false);
      },
      deep: true,
    },
    widgetData: {
      handler() {
        if (!this.isAnyLoading) {
          this.syncDisplayDataFromWidgetData({ cache: false });
        }
      },
      deep: true,
    },
    // The server lists a member's zone only for the user_id in the request URL; a member restored
    // from the session or the saved filter gets it from their own Timeline row instead.
    'widgetData.data.user.time_zone.for': {
      handler(zone) {
        if (zone && +this.widgetData.data.user.id === +this.filters?.user_id) {
          this.applyTimeZoneOptions(this.timeZoneOptionsForCurrentFilter({ timeZone: zone }));
        }
      },
    },
    loadState: {
      handler() {
        this.$emit('loading-changed', this.isLoading);
      },
    },
  },
  created() {
    this.populateTimeZoneOptions();
    this.resetStaleTimeZone();
    this.handleContextChange();
  },
  mounted() {
    this.startRefreshTimer();
    this.trackPageViewed();
  },
  beforeUnmount() {
    this.stopRefreshTimer();
  },
  deactivated() {
    this.stopRefreshTimer();
  },
  activated() {
    this.startRefreshTimer();
  },
  methods: {
    ...mapActions(['fetchWidgets']),

    // Helper method for consistent timeline event tracking
    trackTimelineEvent(eventName, properties = {}) {
      this.trackAnalytics(`Timeline ${eventName}`, {
        ...properties,
        view_type: this.userId ? 'user' : 'team',
        current_page: this.currentPage,
        total_pages: this.pagination?.total_pages || 1,
      });
    },

    clearCache() {
      this.cache = {};
    },

    getProcessedRows() {
      return this.userId ? this.processUserData() : this.processTeamData();
    },

    syncDisplayDataFromWidgetData({ cache = true } = {}) {
      const rows = this.getProcessedRows();
      const pagination = this.widgetData.pagination || null;

      this.displayData = rows;
      this.displayPagination = pagination;

      if (cache && rows.length) {
        this.addToCache(this.cacheKey, rows, pagination);
      }
    },

    addToCache(key, rows, pagination) {
      const keys = Object.keys(this.cache);

      if (keys.length >= MAX_CACHE_SIZE) {
        delete this.cache[keys[0]];
      }
      this.cache[key] = { rows, pagination };
    },

    handleContextChange() {
      this.currentPage = 1;
      this.clearCache();
      this.syncDisplayDataFromWidgetData({ cache: !this.hasInitializedContext });
      this.hasInitializedContext = true;
      this.loadData(false, true);
    },

    startRefreshTimer() {
      this.refreshTimer = setInterval(() => {
        this.refreshData();
      }, REFRESH_INTERVAL);
    },

    stopRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    // Data processing helpers
    processUserData() {
      const widgetData = this.widgetData;
      const dates = widgetData.dates || widgetData.data?.dates || {};
      const userId = widgetData.user?.id || widgetData.data?.user?.id || this.userId;
      const avatar = widgetData.user?.avatar || widgetData.data?.user?.avatar;

      return Object.entries(dates).map(([date, entry]) => ({
        id: date,
        name: date,
        date: entry.date || date,
        total_time: entry.total_time,
        metrics: this.normalizeMetricsData(entry.metrics, userId),
        avatar,
      }));
    },

    processTeamData() {
      const widgetData = this.widgetData;
      const rawData = widgetData?.data;

      if (!rawData) {
        return [];
      }

      const teamData = Array.isArray(rawData)
        ? rawData
        : typeof rawData === 'object'
        ? Object.entries(rawData).map(([k, v]) => ({ ...v, id: k }))
        : [];

      return teamData.map((item) => ({
        ...item,
        date: item.date,
        metrics: this.normalizeMetricsData(item.metrics, item.id),
      }));
    },

    normalizeMetrics(metricKeys) {
      // Map UI metric names to API metric names
      const mappedMetrics = metricKeys.map(metric => METRIC_API_MAPPING[metric] || metric);

      // Remove duplicates using Set
      return [...new Set(mappedMetrics)];
    },

    normalizeMetricsData(metrics, userId) {
      if (!metrics) {return {};}

      return Object.fromEntries(
        Object.entries(metrics).map(([metricKey, metricData]) => [
          metricKey,
          (metricData || []).map((item) => ({
            ...item,
            user_id: userId,
            ...(item.activity_level !== undefined && { activity_level: item.activity_level || 0 })
          }))
        ])
      );
    },

    // UI event handlers
    toggleTotalHours() {
      this.showTotalHours = !this.showTotalHours;

      this.trackTimelineEvent('Column dropdown selected', {
        column: 'total_hours',
        is_visible: this.showTotalHours,
      });
    },

    toggleMetric(key) {
      const index = this.selectedMetricKeys.indexOf(key);
      const checked = index === -1;

      if (checked) {
        this.selectedMetricKeys.push(key);
      } else {
        this.selectedMetricKeys.splice(index, 1);
      }
      this.trackTimelineEvent('Activity type checkbox clicked', {
        activity_type: key,
        is_checked: checked,
        selected_activity_types: this.selectedMetricKeys,
      });
    },

    async sortMetrics(sortOptions) {
      this.sortOptions = sortOptions;
      this.currentPage = 1;
      this.loadData(false, false);
    },

    onPageChanged(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.loadData(false, false);
      }
    },

    async loadData(force = false, isFullLoad = false, { bypassCache = false } = {}) {
      if (this.selectedMetricKeys.length === 0) {
        this.loadState = LOAD_STATE.IDLE;
        this.queuedLoad = null;
        this.queuedLoads.forEach(({ resolve }) => resolve());
        this.queuedLoads = [];

        return;
      }
      const key = this.cacheKey;

      if (!force && !bypassCache && key in this.cache) {
        const cached = this.cache[key];

        this.displayData = cached.rows;
        this.displayPagination = cached.pagination;

        return;
      }
      const requestedLoadState = isFullLoad && !this.displayData.length ? LOAD_STATE.LOADING_FULL : LOAD_STATE.LOADING_ROWS;

      // Queue requests made while another request is in-flight and process them sequentially.
      if (this.isAnyLoading) {
        this.queuedLoad = { force, isFullLoad };

        return new Promise((resolve) => {
          this.queuedLoads.push({ force, isFullLoad, bypassCache: true, resolve });
        });
      }
      this.loadState = requestedLoadState;
      const metrics = this.normalizeMetrics(this.selectedMetricKeys);

      try {
        await this.fetchWidgets({
          widgetKey: this.widgetKey,
          extraQueryParams: {
            ...this.sortOptions,
            page: this.currentPage,
            ...(this.isMembersTimeZoneMode ? {} : { time_zone: this.timeZone }),
            metrics,
          },
        });

        const rows = this.getProcessedRows();
        const pagination = this.widgetData.pagination || null;

        this.addToCache(key, rows, pagination);

        if (key === this.cacheKey) {
          this.displayData = rows;
          this.displayPagination = pagination;
        }
      } catch (error) {
        this.$appMessage(error?.responseJSON?.alert ?? 'An error has occurred');
      } finally {
        this.loadState = LOAD_STATE.IDLE;

        if (this.queuedLoads.length) {
          this.processQueuedLoads();
        } else {
          this.queuedLoad = null;
        }
      }
    },

    async processQueuedLoads() {
      if (this.isProcessingQueuedLoads) {
        return;
      }
      this.isProcessingQueuedLoads = true;

      try {
        while (this.queuedLoads.length) {
          const request = this.queuedLoads.shift();

          this.queuedLoad = this.queuedLoads.length
            ? {
              force: this.queuedLoads[this.queuedLoads.length - 1].force,
              isFullLoad: this.queuedLoads[this.queuedLoads.length - 1].isFullLoad,
            }
            : null;

          await this.loadData(request.force, request.isFullLoad, { bypassCache: request.bypassCache });
          request.resolve();
        }
      } finally {
        this.isProcessingQueuedLoads = false;
        this.queuedLoad = null;
      }
    },
    trackPageViewed() {
      const rowCount = this.data.length;

      this.trackTimelineEvent('Page Viewed', {
        default_activity_types: this.selectedMetricKeys,
        row_count: rowCount,
      });
    },
    async refreshData() {
      if (dayjs().isSame(dayjs(this.date), 'day')) {
        await this.loadData(true, false);
      }
    },
    handleTimelineUserSelected(userId, timeZone) {
      this.$emit('timeline-user-selected', userId, timeZone);
    }
  },
};
</script>
